import { useMemo } from 'react';
import styles from './styles.module.scss';
import {
  filterFullyDecided,
  getAverageScore,
  NUM_OF_EVALUATIONS_WHEN_DECIDED,
} from '../chartView/stats';
import { Col, Container, Row } from 'react-bootstrap';
import { getProgressPercentageDouble } from '../../../helpers/statistic';
import { SESSION_TYPES } from '../../../models/session';
import { useTranslation } from 'react-i18next';

const BucketView = ({
  submissions,
  session,
  predecessorSessionSubmissions,
}) => {
  const { t } = useTranslation();

  const data = useMemo(() => {
    if (!submissions?.length || !session) return null;

    const fullyDecidedSubmissions = filterFullyDecided(
      submissions,
      session?.sessionType
    );

    const regularSubmissionsUndecided = submissions.filter(
      (submission) =>
        !submission?.isCooperation &&
        !submission?.isWildcard &&
        submission?.numDecidedEvaluations <
          NUM_OF_EVALUATIONS_WHEN_DECIDED[session?.sessionType]
    );
    const cooperationsUndecided = submissions.filter(
      (submission) =>
        submission?.isCooperation &&
        submission?.numDecidedEvaluations <
          NUM_OF_EVALUATIONS_WHEN_DECIDED[session?.sessionType]
    );
    const wildcardsUndecided = submissions.filter(
      (submission) =>
        submission?.isWildcard &&
        submission?.numDecidedEvaluations <
          NUM_OF_EVALUATIONS_WHEN_DECIDED[session?.sessionType]
    );

    return {
      fullyDecidedSubmissions: {
        countAbsolute: {
          key: 'countAbsolute',
          value: fullyDecidedSubmissions.length,
          label: t('scoreMonitor.bucketView.fullyDecidedBucket.countAbsolute'),
        },
        countRelative: {
          key: 'countRelative',
          value: `${getProgressPercentageDouble(fullyDecidedSubmissions.length, submissions.length)}%`,
          label: t('scoreMonitor.bucketView.fullyDecidedBucket.countRelative'),
        },
        averageScore: {
          key: 'averageScore',
          value: getAverageScore(fullyDecidedSubmissions, session?.sessionType),
          label: t('scoreMonitor.bucketView.fullyDecidedBucket.averageScore'),
        },
      },
      regularUndecidedSubmissions: {
        countAbsolute: {
          key: 'countAbsolute',
          value: regularSubmissionsUndecided.length,
          label: t(
            'scoreMonitor.bucketView.regularUndecidedBucket.countAbsolute'
          ),
        },
        countRelative: {
          key: 'countRelative',
          value: `${getProgressPercentageDouble(regularSubmissionsUndecided.length, submissions.length)}%`,
          label: t(
            'scoreMonitor.bucketView.regularUndecidedBucket.countRelative'
          ),
        },
        averagePreselectionScore: {
          key: 'averagePreselectionScore',
          value: getAverageScore(
            predecessorSessionSubmissions,
            SESSION_TYPES.PRESELECTION
          ),
          label: t(
            'scoreMonitor.bucketView.regularUndecidedBucket.averagePreselectionScore'
          ),
        },
      },
      specialUndecidedSubmissions: {
        cooperationCountAbsolute: {
          key: 'cooperationCount',
          value: cooperationsUndecided.length,
          label: t(
            'scoreMonitor.bucketView.specialUndecidedBucket.cooperationCountAbsolute'
          ),
        },
        cooperationCountRelative: {
          key: 'cooperationCountRelative',
          value: `${getProgressPercentageDouble(cooperationsUndecided.length, submissions.length)}%`,
          label: t(
            'scoreMonitor.bucketView.specialUndecidedBucket.cooperationCountRelative'
          ),
        },
        wildcardCountAbsolute: {
          key: 'wildcardCount',
          value: wildcardsUndecided.length,
          label: t(
            'scoreMonitor.bucketView.specialUndecidedBucket.wildcardCountAbsolute'
          ),
        },
        wildcardCountRelative: {
          key: 'wildcardCountRelative',
          value: `${getProgressPercentageDouble(wildcardsUndecided.length, submissions.length)}%`,
          label: t(
            'scoreMonitor.bucketView.specialUndecidedBucket.wildcardCountRelative'
          ),
        },
      },
    };
  }, [submissions, session, predecessorSessionSubmissions]);

  return (
    <Container>
      <Row className="my-4">
        <Col xs={12} sm={12} md={12} lg={12}>
          <div className={styles.statContainer}>
            <div className={styles.statContainerTitle}>
              {t('scoreMonitor.bucketView.fullyDecidedBucket.title')}
            </div>
            <div className={styles.statContainerContent}>
              {Object.values(data?.fullyDecidedSubmissions).map((stat) => (
                <div className={styles.statContainerItem} key={stat.key}>
                  <span className={styles.label}>{stat.label}</span>
                  <span className={styles.value}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
      <Row className="my-4">
        <Col xs={12} sm={12} md={12} lg={12}>
          <div className={styles.statContainer}>
            <div className={styles.statContainerTitle}>
              {t('scoreMonitor.bucketView.regularUndecidedBucket.title')}
            </div>
            <div className={styles.statContainerContent}>
              {Object.values(data?.regularUndecidedSubmissions).map((stat) => (
                <div className={styles.statContainerItem} key={stat.key}>
                  <span className={styles.label}>{stat.label}</span>
                  <span className={styles.value}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
      <Row className="my-4">
        <Col xs={12} sm={12} md={12} lg={12}>
          <div className={styles.statContainer}>
            <div className={styles.statContainerTitle}>
              {t('scoreMonitor.bucketView.specialUndecidedBucket.title')}
            </div>
            <div className={styles.statContainerContent}>
              {Object.values(data?.specialUndecidedSubmissions).map((stat) => (
                <div className={styles.statContainerItem} key={stat.key}>
                  <span className={styles.label}>{stat.label}</span>
                  <span className={styles.value}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BucketView;
